import { AsIsTableDataTransform, ColumnConfig, ColumnText, ConditionalExtractor, DateFormatter, ExtractAndFormatExtractor, LabelFormatter, LodashExtractor, LodashSeriesEnricherMapper, NumberFormatter, TabConfig, TablePerPortfolioSeriesExtractorConfig } from '@ddt-web/data-service';
import { SharedApplicationConstants } from '@ddt-web/domain';
import { DateUtil } from '@ddt-web/generic-utils';

import { FAMILY_COLUMN_CONFIG, FAMILY_HOLDING_COLUMN_CONFIG, HOLDINGS_ENRICHER } from './family-column.config';


const MODEL_PORTFOLIO_FILTER_BY_GROUP_ID = (isBLK: boolean) => (portfolio: any, data: any) => {
	const groups = data?.families?.groups ?? portfolio?.groups;
	const groupIdArray = groups?.map(element => element.id) ?? [];
	return (isBLK) ? groupIdArray.includes(SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP) :
		(groupIdArray.length === 1) ? !groupIdArray.includes(SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP) : groupIdArray.length > 1;
};

const EXCEL_HOLDINGS_MODELS_COLUMN_CONFIG = [
	ColumnConfig.create({
		label: new ColumnText('', '', null, { useFormattedValueForExcel: true }),
		extractor: new ConditionalExtractor(
			model => model.managementType === 'research' && model.pkg !== 'ACT-LAM',
			new ExtractAndFormatExtractor('shortName', '', (description) => '*' + description),
			new LodashExtractor('shortName', '')
		),
		id: 'sn'
	}),
	ColumnConfig.create({
		label: new ColumnText('', '', null, { useFormattedValueForExcel: true }),
		extractor: new LodashExtractor('familyName'),
		id: 'fn'
	}),
	ColumnConfig.create({
		label: new ColumnText('', '', null, { useFormattedValueForExcel: true }),
		extractor: new LodashExtractor('displayOrder'),
		id: 'do',
		visible: false
	}),
	ColumnConfig.create({
		label: new ColumnText('', '', null, { useFormattedValueForExcel: true }),
		extractor: new LodashExtractor('groups'),
		id: 'g',
		visible: false
	}),
	ColumnConfig.create({
		label: new ColumnText('', '', null, { useFormattedValueForExcel: true }),
		extractor: new LodashExtractor('id'),
		id: 'id',
		visible: false
	}),
	ColumnConfig.create({
		label: new ColumnText('', '', null, { useFormattedValueForExcel: true }),
		extractor: new LodashExtractor('familyId'),
		id: 'fid',
		visible: false
	}),
	ColumnConfig.create({
		label: new ColumnText('', '', null, { useFormattedValueForExcel: true }),
		extractor: new LodashExtractor('modelExternalName'),
		id: 'modelExternalNameMapId',
		visible: false
	})
];

const HOLDINGS_MODEL_DATA_ENRICHER = (portfolio: any, data: any) => {
	const groups = data?.families?.groups ?? portfolio?.groups;
	return {
		...portfolio,
		familyName: data?.families?.description,
		displayOrder: data?.families?.displayOrder,
		familyId: data?.families?.id,
		groups: (groups?.length === 1 && groups?.[0].id === 'blackrock_family_group') ? 'BLK' : 'CMS',
		id: portfolio?.id,
		managementType: portfolio?.managementType || data?.families?.managementType,
		modelExternalName: data?.families?.modelExternalName
	};
};

const EXCEL_HOLDINGS_DETAILS_COMMON_COLUMNS = [ColumnConfig.create({
	label: new ColumnText('', '', null, { useFormattedValueForExcel: true }),
	extractor: new LodashExtractor('formattedAcAssetClass', 'Other'),
	id: 'ac',
	labelFormatter: new LabelFormatter('HOLDINGS_CHART')
}),
ColumnConfig.create({
	label: new ColumnText('', '', null, { useFormattedValueForExcel: true }),
	extractor: new LodashExtractor('posInfo.ticker.stringValue'),
	id: 't'
}),
ColumnConfig.create({
	label: new ColumnText('', '', null),
	extractor: new LodashExtractor('weight'),
	id: 'w',
	labelFormatter: new NumberFormatter(NumberFormatter.PERCENT)
}),
ColumnConfig.create({
	label: new ColumnText('', '', null),
	extractor: new LodashExtractor('changesToHoldings'),
	id: 'c',
	labelFormatter: new NumberFormatter(NumberFormatter.PERCENT)
}),
ColumnConfig.create({
	label: new ColumnText('', '', null, { useFormattedValueForExcel: true }),
	extractor: new LodashExtractor('posInfo.issueName.stringValue'),
	id: 'in'
})];

export const EXCEL_BLK_HOLDINGS_MODEL_DATA = new TabConfig(
	'excel-blk-holdings-model-data',
	null,
	new TablePerPortfolioSeriesExtractorConfig(
		new LodashSeriesEnricherMapper(['families'], ['families'], 'families.models', HOLDINGS_MODEL_DATA_ENRICHER, MODEL_PORTFOLIO_FILTER_BY_GROUP_ID(true)),
		null,
		EXCEL_HOLDINGS_MODELS_COLUMN_CONFIG,
		FAMILY_COLUMN_CONFIG
	)
);

export const EXCEL_CMS_HOLDINGS_MODEL_DATA = new TabConfig(
	'excel-cms-holdings-model-data',
	null,
	new TablePerPortfolioSeriesExtractorConfig(
		new LodashSeriesEnricherMapper(['families'], ['families'], 'families.models', HOLDINGS_MODEL_DATA_ENRICHER, MODEL_PORTFOLIO_FILTER_BY_GROUP_ID(false)),
		null,
		EXCEL_HOLDINGS_MODELS_COLUMN_CONFIG,
		FAMILY_COLUMN_CONFIG
	)
);

export const EXCEL_HOLDINGS_DETAILS = new TabConfig(
	'excel-holdings-details',
	null,
	new TablePerPortfolioSeriesExtractorConfig(
		new LodashSeriesEnricherMapper(['familyHoldings', 'positionDetails'], ['familyHoldings', 'positionDetails'], 'familyHoldings.components', HOLDINGS_ENRICHER),
		null,
		[
			ColumnConfig.create({
				label: new ColumnText('', '', null, { useFormattedValueForExcel: true }),
				extractor: new LodashExtractor('asOfDate'),
				id: 'haod',
				labelFormatter: new DateFormatter()
			}),
			...EXCEL_HOLDINGS_DETAILS_COMMON_COLUMNS
		],
		FAMILY_HOLDING_COLUMN_CONFIG,
		null,
		AsIsTableDataTransform.instance
	)
);

export const EXCEL_HOLDINGS_DETAILS_AU = new TabConfig(
	'excel-holdings-details',
	null,
	new TablePerPortfolioSeriesExtractorConfig(
		new LodashSeriesEnricherMapper(['familyHoldings', 'positionDetails'], ['familyHoldings', 'positionDetails'], 'familyHoldings.components', HOLDINGS_ENRICHER),
		null,
		[
			ColumnConfig.create({
				label: new ColumnText('', '', null, { useFormattedValueForExcel: true }),
				extractor: new LodashExtractor('asOfDate'),
				id: 'haod',
				labelFormatter: new DateFormatter(DateUtil.MODELS_EU_DATE_FORMAT)
			}),
			...EXCEL_HOLDINGS_DETAILS_COMMON_COLUMNS
		],
		FAMILY_HOLDING_COLUMN_CONFIG,
		null,
		AsIsTableDataTransform.instance
	)
);
